// material-ui
import { alpha } from '@mui/material/styles';

// project imports
import componentsOverrides from './overrides';
import { ThemeMode } from 'config';
import { fill } from 'lodash';
import { Height } from '@mui/icons-material';
import { color } from 'd3';
export default function componentStyleOverrides(theme, borderRadius, outlinedFilled) {
  const mode = theme.palette.mode;
  const bgColor = mode === ThemeMode.DARK ? theme.palette.dark[800] : 'transparent';
  const menuSelectedBack = mode === ThemeMode.DARK ? alpha(theme.palette.secondary.main, 1) : theme.palette.secondary.light;
  const menuSelected = mode === ThemeMode.DARK ? theme.palette.dark.light : theme.palette.grey[900];

  return {
    
    MuiButtonBase: {
      styleOverrides: {
        root: {          
          '&.MuiMenuItem-root': {
            minHeight: 'auto',
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '4px',
          '&.MuiButton-sizeSmall': {
            height: '30px',
            minWidth: '47px',
            fontSize: theme.typography.body1.fontSize,
          },
          '&:hover': {
            boxShadow: 'none',
          },
          ...theme.applyStyles('dark', {
            '&.MuiButton-colorWarning': { color: theme.palette.common.black }
          })
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '& + .MuiFormControlLabel-label': {
            marginTop: 2
          }
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        },
        rounded: {
          borderRadius: `${borderRadius}px`
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          padding: '24px'
        },
        title: {
          fontSize: theme.typography.body2.fontSize,
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px 24px'
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px'
        }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
          fontSize: theme.typography.body1.fontSize,
          '&.MuiAlert-standardError' : {
            backgroundColor: mode === ThemeMode.DARK ? alpha(theme.palette.error.dark, 0.15) : theme.palette.error[50],
          },
          '&.MuiAlert-colorError': {
            color: mode === ThemeMode.DARK ? alpha(theme.palette.error.dark, 0.6) : theme.palette.error.dark,
            '& .MuiAlert-icon': {
              color: mode === ThemeMode.DARK ? alpha(theme.palette.error.dark, 0.6) : theme.palette.error.dark,
            }
          },
          '&.MuiAlert-colorInfo': {
            backgroundColor: mode === ThemeMode.DARK ? '#02191a' : theme.palette.cyan[50],
            color: mode === ThemeMode.DARK ? alpha(theme.palette.cyan[900], 1) : theme.palette.cyan[900],
            '& .MuiAlert-icon': {
              color: mode === ThemeMode.DARK ? alpha(theme.palette.cyan[900], 0.9) : theme.palette.cyan[900],
            }
          },
        },
        outlined: {
          border: '1px dashed'
        },
        filled: {
          '&.MuiAlert-colorError': {
            color: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.8) : theme.palette.white.paper,
            '& .MuiAlert-icon': {
              color: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.8) : theme.palette.white.paper,
            }
          },
          '&.MuiAlert-colorWarning': {
            backgroundColor: mode === ThemeMode.DARK ? '#3b2102' : theme.palette.warning.main,
            color: mode === ThemeMode.DARK ? alpha(theme.palette.warning.dark, 0.8) : theme.palette.warning.dark,
            '& .MuiAlert-icon': {
              color: mode === ThemeMode.DARK ? alpha(theme.palette.warning.dark, 0.8) : theme.palette.warning.dark,
            }
          },
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: menuSelected,
          paddingTop: '10px',
          paddingBottom: '10px',
          '&.Mui-selected': {
            color: menuSelected,
            backgroundColor: menuSelectedBack,
            '&:hover': {
              backgroundColor: menuSelectedBack
            },
            '& .MuiListItemIcon-root': {
              color: menuSelected
            }
          },
          '&:hover': {
            backgroundColor: mode === ThemeMode.DARK ? alpha(theme.palette.white.paper, 0.08) : 'grey.200',
            color: menuSelected,
            '& .MuiListItemIcon-root': {
              color: menuSelected
            }
          }

        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[900],
          minWidth: '36px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: theme.palette.text.dark
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: theme.palette.text.dark,
          '&::placeholder': {
            color: theme.palette.text.secondary,
            fontSize: theme.typography.body1.fontSize,
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: bgColor,
          borderRadius: `${borderRadius}px`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.5) : theme.palette.grey[400]
          },
          '&:hover $notchedOutline': {
            borderColor: theme.palette.primary.light
          },
          '&.MuiInputBase-multiline': {
            padding: 1
          }
        },
        input: {
          background: bgColor,
          padding: '8px 14px',
          borderRadius: `${borderRadius}px`,
          '&.MuiInputBase-inputSizeSmall': {
            padding: '8px 14px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0
            }
          }
        },
        inputAdornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: `${borderRadius}px`
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.5) : theme.palette.grey[300]
          }
        },
        mark: {
          backgroundColor: theme.palette.background.paper,
          width: '2px',
        },
        valueLabel: {
          color: mode === ThemeMode.DARK ? theme.palette.dark.dark : theme.palette.grey[900],
          fontSize: theme.typography.body1.fontSize,
          fontWeight:'400',
          backgroundColor:'#A7EFD8',
          padding: '3px 8px'
        },
        markLabel:{
          fontSize: theme.typography.body1.fontSize, 
        }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-tag': {
            background: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.2) : theme.palette.secondary.light,
            borderRadius: 4,
            color: theme.palette.text.dark,
            '.MuiChip-deleteIcon': {
              color: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.8) : theme.palette.secondary[200]
            }
          }
        },
        popper: {
          borderRadius: `${borderRadius}px`,
          boxShadow: '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent'
          }
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: mode === ThemeMode.DARK ? theme.palette.dark.main : theme.palette.primary.dark,
          background: mode === ThemeMode.DARK ? theme.palette.text.primary : theme.palette.primary[200]
        }
      }
    },
    MuiTimelineContent: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          fontSize: theme.typography.body1.fontSize,
        }
      }
    },
    MuiTreeItem: {
      styleOverrides: {
        label: {
          marginTop: 14,
          marginBottom: 14
        }
      }
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          boxShadow: 'none'
        }
      }
    },
    MuiInternalDateTimePickerTabs: {
      styleOverrides: {
        tabs: {
          backgroundColor: mode === ThemeMode.DARK ? theme.palette.dark[900] : theme.palette.primary.light,
          '& .MuiTabs-flexContainer': {
            borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.2) : theme.palette.primary[200]
          },
          '& .MuiTab-root': {
            color: mode === ThemeMode.DARK ? theme.palette.text.secondary : theme.palette.grey[900]
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.dark
          },
          '& .Mui-selected': {
            color: theme.palette.primary.dark
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          borderBottom: '1px solid',
          borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.divider, 0.08) : theme.palette.grey[200]
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '12px 0 12px 0'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.text.primary, 0.15) : theme.palette.grey[200],
          fontSize: theme.typography.body1.fontSize,
          color: theme.palette.grey[800],
          '&.MuiTableCell-head': {
            fontSize: theme.typography.body1.fontSize,
            color: theme.palette.grey[800],
            fontWeight: 500,
            '& .MuiTableSortLabel-root': {
              color: theme.palette.grey[800],
            }
          }
        }
      }
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: {
        timeDigitsContainer: {
          alignItems: 'center'
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          margin: 0,
          lineHeight: 1.4,
          background: mode === ThemeMode.DARK ? '#2C2B30' : theme.palette.white.paper,
          color: mode === ThemeMode.DARK ? theme.palette.white.paper : theme.palette.text.primary,
          fontSize: theme.typography.body1.fontSize,
          fontWeight: 400,
          padding: '7px 11px',
          boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.14)',
          borderRadius: '10px',
          border: `1px solid ${mode === ThemeMode.DARK ? '#4d4d4d' : 'transparent'}`,
          '& .MuiTooltip-arrow': {
          color: mode === ThemeMode.DARK ? '#2C2B30' : theme.palette.white.paper,
        }
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: theme.typography.body2.fontSize,
        }
      }
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          margin: '3px'
        }
      }
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          '&.MuiSnackbar-anchorOriginTopCenter': {
            '@media (min-width:600px)': {
              top: '80px',
            },
          }
        }
      }
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          '&[data-testid="VisibilityIcon"]': {
            fontSize: '18px',
          },
          '&[data-testid="VisibilityOffIcon"]': {
            fontSize: '18px',
          },
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 54
      },
      styleOverrides: {
        root: {
          borderWidth: 0,
          backgroundColor: theme.palette.background.paper,
          '& .MuiDataGrid-columnHeader--filledGroup': {
            borderBottomWidth: 0
          },
          '& .MuiDataGrid-columnHeader--emptyGroup': {
            borderBottomWidth: 0
          },
          '& .MuiFormControl-root>.MuiInputBase-root': {
            backgroundColor: `${theme.palette.background.default} !important`,
            borderColor:
              mode === ThemeMode.DARK ? `${alpha(theme.palette.divider, 0.05)} !important` : `${theme.palette.divider} !important`
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: theme.palette.background.paper,
            },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: mode === ThemeMode.DARK ? theme.palette.background.default : 'undefined',
            '&:hover': {
              backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[150] : 'undefined',
            },
            '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: mode === ThemeMode.DARK ? theme.palette.background.default : 'undefined',
            },
            '&:hover .MuiDataGrid-cell--pinnedLeft, &:hover .MuiDataGrid-cell--pinnedRight': {
              backgroundColor: mode === ThemeMode.DARK ? theme.palette.grey[150] : 'undefined',
            }
          }

        },
        withBorderColor: {
          borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.divider, 0.15) : theme.palette.divider
        },
        toolbarContainer: {
          '& .MuiButton-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important'
          }
        },
        columnHeader: {
          color: theme.palette.grey[800],
          paddingLeft: 16,
          paddingRight: 16,
          background: theme.palette.grey[150],
          fontSize: theme.typography.body1.fontSize,
        },
        footerContainer: {
          '&.MuiDataGrid-withBorderColor': {
            borderBottom: 'none'
          }
        },
        columnHeaderCheckbox: {
          paddingLeft: 0,
          paddingRight: 0
        },
        cellCheckbox: {
          paddingLeft: 0,
          paddingRight: 0
        },
        cell: {
          borderWidth: 1,
          paddingLeft: 16,
          paddingRight: 16,        
          fontSize: theme.typography.body1.fontSize,
          color: theme.palette.grey[800],
          borderColor: mode === ThemeMode.DARK ? alpha(theme.palette.divider, 1) : theme.palette.divider,
          '&.MuiDataGrid-cell--withRenderer > div ': {
            ...(mode === ThemeMode.DARK && {
              color: theme.palette.grey[50]
            }),
            ' > .high': {
              background: mode === ThemeMode.DARK ? theme.palette.success.dark : theme.palette.success.light
            },
            '& > .medium': {
              background: mode === ThemeMode.DARK ? theme.palette.warning.dark : theme.palette.warning.light
            },
            '& > .low': {
              background: mode === ThemeMode.DARK ? theme.palette.error.dark : theme.palette.error.light
            }
          }
        }
      }
    },
    ...componentsOverrides(theme)
  };
}
